import { useEffect, useRef, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { onUnauthenticated } from '@/api/events'
import { ApiError } from '@/api/errors'
import { clearUserCaches, useMe } from '@/api/hooks'
import i18n, { isAppLocale, setLocale } from '@/i18n'
import { ThemeProvider, useTheme } from '@/lib/theme-provider'
import { ToastProvider } from '@/components/ui/toast'
import { TooltipProvider } from '@/components/ui/tooltip'

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (count, error) => !(error instanceof ApiError && error.status < 500 && error.status !== 0) && count < 2,
      },
    },
  })
}

/** After sign-in the profile wins for language and theme (once per user). */
function ProfileSync() {
  const { data: me } = useMe()
  const { setPreference } = useTheme()
  const syncedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!me || syncedFor.current === me.id) return
    syncedFor.current = me.id
    if (isAppLocale(me.locale) && me.locale !== i18n.language) void setLocale(me.locale)
    setPreference(me.theme)
  }, [me, setPreference])
  return null
}

/** 401 anywhere: clear user-scoped caches; route guards then redirect to /login. */
function UnauthenticatedWatcher() {
  const queryClient = useQueryClient()
  useEffect(() => onUnauthenticated(() => clearUserCaches(queryClient)), [queryClient])
  return null
}

export function AppProviders({ client, children }: { client: QueryClient; children: ReactNode }) {
  return (
    <QueryClientProvider client={client}>
      <ThemeProvider>
        <TooltipProvider delayDuration={400}>
          <ToastProvider>
            <ProfileSync />
            <UnauthenticatedWatcher />
            {children}
          </ToastProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
